/**
 * One value-noise field over world XZ, as a TSL node, shared by everything in
 * this port that has to make a large flat grey surface look like cloud.
 *
 * Two callers: `flight/overcast-deck.ts`'s ENHANCED stratus sheet and
 * `flight/sky-clouds.ts`'s ENHANCED paint on the original's own slabs. It is
 * here rather than copied for the reason `three/ordered-dither.ts` states about
 * its Bayer ladder — a second implementation of a stated rule is a place for a
 * bug to hide with a good explanation attached — and for one specific to this
 * field: the deck and a slab can be in the same frame at the same altitude, and
 * two clouds made of two different noises would not read as one weather.
 *
 * ## Why world XZ and not the surface's own UV
 *
 * A cloud has to be **anchored to the ground**. Read off a quad's UV, the
 * pattern is glued to the geometry — which for `overcast-deck.ts`'s
 * camera-following sheet means glued to the pilot, so the deck would slide
 * overhead as one rigid printed card and the sky would have no parallax at all.
 * `positionWorld` is the fragment's real world coordinate after every transform,
 * so a given patch of cloud keeps its shape and its place while the geometry
 * underneath it moves. It also makes the two callers agree for free: a slab and
 * the deck at the same spot get the same value.
 *
 * ## Why a sine hash is enough
 *
 * The field's only consumers are `step`s onto a three-entry palette ramp, so
 * the periodicity a `fract(sin(...))` hash is criticised for cannot show up as
 * anything finer than "the cloud is slightly regular" — which a stratus deck is.
 * Nothing here is sampled for a distribution, saved, or compared across
 * platforms.
 */

import * as THREE from "three/webgpu";
import type { FloatNode } from "./ordered-dither.ts";

/**
 * The octaves' feature sizes in metres, and their weights.
 *
 * 3 km is the size of a stratus break seen from underneath, each octave below is
 * a little over half the one before with about half the weight, and the four sum
 * to 1 so the field's range is `[0, 1]` and its mean is 0.5 — which is what lets
 * the callers' step thresholds be read as "this fraction of the cloud".
 *
 * Stated as lengths rather than as a lacunarity and a gain, for
 * `enhanced-sky-look.ts`'s reason: a length is a number someone can check
 * against the screen and an exponent is not.
 *
 * **The 190 m octave is not decoration.** A `MISC-10` slab is 11.6 km across and
 * a pilot skimming its top sees it at a grazing angle, so the 3 km octave can be
 * *wider than the screen* — measured on 2026-08-24, a slab lit from above came
 * out as one uncut `#d7d7d7` field with only the three coarse octaves running.
 * The finest one is what gives the near half of a surface anything to look at.
 */
export const OCTAVE_METRES: readonly number[] = [3000, 1200, 480, 190];
export const OCTAVE_WEIGHTS: readonly number[] = [0.45, 0.27, 0.18, 0.1];

/** `fract(sin(dot(cell, k)) * m)` — the standard one-line hash, per the header. */
function hash01(cell: THREE.Node<"vec2">): FloatNode {
  return THREE.TSL.fract(THREE.TSL.sin(THREE.TSL.dot(cell, THREE.TSL.vec2(127.1, 311.7))).mul(43758.5453));
}

/** Bilinear value noise over one octave, smoothstepped inside the cell so the lattice leaves no creases. */
function valueNoise(point: THREE.Node<"vec2">): FloatNode {
  const cell = THREE.TSL.floor(point);
  const inside = THREE.TSL.fract(point);
  // `3f² − 2f³` written as `f²(3 − 2f)`, the same smoothstep every other shader here uses.
  const weight = inside.mul(inside).mul(THREE.TSL.vec2(3, 3).sub(inside.mul(2)));
  const lowX = THREE.TSL.mix(hash01(cell), hash01(cell.add(THREE.TSL.vec2(1, 0))), weight.x);
  const highX = THREE.TSL.mix(
    hash01(cell.add(THREE.TSL.vec2(0, 1))),
    hash01(cell.add(THREE.TSL.vec2(1, 1))),
    weight.x,
  );
  return THREE.TSL.mix(lowX, highX, weight.y);
}

/**
 * The field at this fragment's own world position, in `[0, 1]` with mean 0.5.
 *
 * Call it from a `colorNode`; it is three hashes' worth of arithmetic per
 * octave and no texture fetch, so it costs nothing a large flat quad was not
 * already paying for.
 */
export function cloudFieldAtFragment(): FloatNode {
  const flat = THREE.TSL.vec2(THREE.TSL.positionWorld.x, THREE.TSL.positionWorld.z);
  let sum: FloatNode | undefined;
  OCTAVE_METRES.forEach((metres, octave) => {
    const term = valueNoise(flat.div(metres)).mul(OCTAVE_WEIGHTS[octave] ?? 0);
    sum = sum ? sum.add(term) : term;
  });
  // Unreachable while `OCTAVE_METRES` is non-empty; a flat half-cover field is
  // the honest fallback rather than a throw inside a shader build.
  return sum ?? THREE.TSL.dot(THREE.TSL.vec2(0.5, 0), THREE.TSL.vec2(1, 0));
}

/**
 * How the octave weights sum, so `cloud-noise.test.ts` can pin that the field
 * really does span `[0, 1]` — the property both callers' thresholds are read
 * against — without needing a GPU to evaluate the node.
 */
export function octaveWeightSum(): number {
  return OCTAVE_WEIGHTS.reduce((total, weight) => total + weight, 0);
}
